import { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../../api/client";
import QuestionMedia from "./QuestionMedia";

// ── Answer input components ───────────────────────────────────────────────────

function YesNoInput({ value, onChange, disabled }) {
  const btn = (v, label, activeClass) => (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(value === v ? null : v)}
      className={[
        "px-4 py-1.5 text-sm border transition",
        "first:rounded-l last:rounded-r",
        value === v
          ? activeClass
          : "bg-white text-gray-700 hover:bg-gray-50",
        disabled ? "opacity-60 cursor-not-allowed" : "",
      ].join(" ")}
    >
      {label}
    </button>
  );
  return (
    <div className="inline-flex shadow-sm">
      {btn("yes", "Yes", "bg-green-600 text-white border-green-600")}
      {btn("no",  "No",  "bg-red-500 text-white border-red-500")}
    </div>
  );
}

function YesNoNaInput({ value, onChange, disabled }) {
  const btn = (v, label, activeClass) => (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(value === v ? null : v)}
      className={[
        "px-4 py-1.5 text-sm border transition",
        "first:rounded-l last:rounded-r",
        value === v
          ? activeClass
          : "bg-white text-gray-700 hover:bg-gray-50",
        disabled ? "opacity-60 cursor-not-allowed" : "",
      ].join(" ")}
    >
      {label}
    </button>
  );
  return (
    <div className="inline-flex shadow-sm">
      {btn("yes", "Yes", "bg-green-600 text-white border-green-600")}
      {btn("no",  "No",  "bg-red-500 text-white border-red-500")}
      {btn("na",  "N/A", "bg-gray-500 text-white border-gray-500")}
    </div>
  );
}

function NumericInput({ value, onChange, disabled, min, max }) {
  return (
    <input
      type="number"
      disabled={disabled}
      value={value ?? ""}
      min={min ?? undefined}
      max={max ?? undefined}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      className="border rounded px-3 py-1.5 text-sm w-36 focus:outline-none focus:ring-1 focus:ring-gray-400 disabled:bg-gray-50 disabled:opacity-60"
    />
  );
}

function TextInput({ value, onChange, disabled }) {
  return (
    <textarea
      disabled={disabled}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      rows={2}
      className="border rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-1 focus:ring-gray-400 disabled:bg-gray-50 disabled:opacity-60 resize-none"
    />
  );
}

function SingleChoiceInput({ value, onChange, disabled, options }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          disabled={disabled}
          onClick={() => onChange(value === String(opt.id) ? null : String(opt.id))}
          className={[
            "px-3 py-1.5 text-sm border rounded-full transition",
            value === String(opt.id)
              ? "bg-gray-900 text-white border-gray-900"
              : "bg-white text-gray-700 hover:bg-gray-50",
            disabled ? "opacity-60 cursor-not-allowed" : "",
          ].join(" ")}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function MultiChoiceInput({ value, onChange, disabled, options }) {
  // value is a JSON string of array, or null
  const selected = useMemo(() => {
    try { return Array.isArray(value) ? value : JSON.parse(value || "[]"); }
    catch { return []; }
  }, [value]);

  function toggle(optId) {
    const s = String(optId);
    const next = selected.includes(s)
      ? selected.filter((x) => x !== s)
      : [...selected, s];
    onChange(next.length ? JSON.stringify(next) : null);
  }

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = selected.includes(String(opt.id));
        return (
          <button
            key={opt.id}
            type="button"
            disabled={disabled}
            onClick={() => toggle(opt.id)}
            className={[
              "px-3 py-1.5 text-sm border rounded-full transition",
              active
                ? "bg-gray-900 text-white border-gray-900"
                : "bg-white text-gray-700 hover:bg-gray-50",
              disabled ? "opacity-60 cursor-not-allowed" : "",
            ].join(" ")}
          >
            {active ? "✓ " : ""}{opt.label}
          </button>
        );
      })}
    </div>
  );
}

function DateInput({ value, onChange, disabled }) {
  return (
    <input
      type="date"
      disabled={disabled}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      className="border rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 disabled:bg-gray-50 disabled:opacity-60"
    />
  );
}

// ── Helper: get display value from answer row ─────────────────────────────────

function getAnswerValue(qtype, row) {
  if (!row) return null;
  if (qtype === "yes_no") {
    if (row.value_bool === true) return "yes";
    if (row.value_bool === false) return "no";
    return null;
  }
  if (qtype === "yes_no_na") return row.value_choice || null;
  if (qtype === "numeric") return row.value_numeric ?? null;
  if (qtype === "text") return row.value_text || null;
  if (qtype === "single_choice") return row.value_choice || null;
  if (qtype === "multi_choice") return row.value_multi_choice || null;
  if (qtype === "date") return row.value_text || null;
  return null;
}

// ── Helper: build queueSave patch from value ──────────────────────────────────

function buildPatch(qtype, newValue) {
  switch (qtype) {
    case "yes_no":
      return {
        value_bool: newValue === "yes" ? true : newValue === "no" ? false : null,
        value_choice: null,
      };
    case "yes_no_na":
      return { value_choice: newValue, value_bool: null };
    case "numeric":
      return { value_numeric: newValue, value_bool: null, value_choice: null };
    case "text":
    case "date":
      return { value_text: newValue, value_bool: null, value_choice: null };
    case "single_choice":
      return { value_choice: newValue, value_bool: null };
    case "multi_choice":
      return { value_multi_choice: newValue, value_bool: null, value_choice: null };
    default:
      return {};
  }
}

// ── Helper: check if a question has a meaningful answer ───────────────────────

function hasAnswer(qtype, row) {
  if (!row) return false;
  const v = getAnswerValue(qtype, row);
  if (v === null || v === undefined) return false;
  if (typeof v === "string" && v.trim() === "") return false;
  return true;
}

// ── Single question card ──────────────────────────────────────────────────────

function QuestionCard({
  q, row, locked, inspectionId, token, user,
  queueSave, isFollowUp, depth,
  allQuestions, answersByQid,
}) {
  const qid    = Number(q.id);
  const qtype  = (q.type || "").toLowerCase();
  const value  = getAnswerValue(qtype, row);
  const answered = hasAnswer(qtype, row);

  // Determine which follow-ups to show
  const followUps = useMemo(() =>
    allQuestions.filter(
      (fq) =>
        fq.parent_id === qid &&
        fq.parent_trigger === (value === null ? "__never__" : value)
    ),
    [allQuestions, qid, value]
  );

  function onValueChange(newVal) {
    queueSave(qid, buildPatch(qtype, newVal));
  }
  function onCommentChange(e) {
    queueSave(qid, { comment: e.target.value });
  }

  const inputProps = { value, onChange: onValueChange, disabled: locked, options: q.options || [] };

  return (
    <div className={[
      "border rounded-lg bg-white",
      isFollowUp ? "ml-3 sm:ml-6 border-l-4 border-l-blue-200" : "",
      !answered && q.required ? "border-amber-200" : "border-gray-200",
    ].join(" ")}>
      <div className="px-4 py-3">
        {/* Question header */}
        <div className="flex items-start gap-2 mb-3">
          <div className="flex-1">
            {isFollowUp && (
              <span className="text-xs text-blue-500 font-medium mr-2">
                ↳ Follow-up
              </span>
            )}
            {q.required && !answered && (
              <span className="text-xs text-amber-500 font-medium mr-2">Required</span>
            )}
            <span className="text-sm font-medium">
              {q.code ? <span className="font-mono text-gray-400 mr-1.5 text-xs">{q.code}</span> : null}
              {q.text}
            </span>
            {q.help_text && (
              <div className="text-xs text-gray-500 mt-0.5">{q.help_text}</div>
            )}
          </div>
          {/* Conformity indicator */}
          {answered && (
            <span className={[
              "shrink-0 text-xs px-2 py-0.5 rounded-full font-medium",
              value === "yes" ? "bg-green-50 text-green-700" :
              value === "no"  ? "bg-red-50 text-red-600" :
              value === "na"  ? "bg-gray-100 text-gray-500" :
              "bg-blue-50 text-blue-600",
            ].join(" ")}>
              {value === "yes" ? "✓ Yes" :
               value === "no"  ? "✗ No" :
               value === "na"  ? "N/A" :
               "Answered"}
            </span>
          )}
        </div>

        {/* Answer input */}
        <div className="mb-3">
          {qtype === "yes_no"        && <YesNoInput {...inputProps} />}
          {qtype === "yes_no_na"     && <YesNoNaInput {...inputProps} />}
          {qtype === "numeric"       && <NumericInput {...inputProps} min={q.min_value} max={q.max_value} />}
          {qtype === "text"          && <TextInput {...inputProps} />}
          {qtype === "single_choice" && <SingleChoiceInput {...inputProps} />}
          {qtype === "multi_choice"  && <MultiChoiceInput {...inputProps} />}
          {qtype === "date"          && <DateInput {...inputProps} />}
          {!["yes_no","yes_no_na","numeric","text","single_choice","multi_choice","date"].includes(qtype) && (
            <span className="text-xs text-gray-400 italic">Unknown type: {qtype}</span>
          )}
        </div>

        {/* Comment + media row */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <input
            type="text"
            placeholder="Comment (optional)"
            disabled={locked}
            value={row?.comment || ""}
            onChange={onCommentChange}
            className="flex-1 border rounded px-3 py-1.5 text-sm text-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-300 disabled:bg-gray-50 disabled:opacity-60"
          />
          <QuestionMedia
            inspectionId={inspectionId}
            questionId={qid}
            answerId={row?.id || row?.answer_id}
            token={token}
            createdByUserId={user?.id}
            disabled={locked}
          />
        </div>
      </div>

      {/* Follow-up questions */}
      {followUps.length > 0 && (
        <div className="border-t border-blue-100 px-4 py-3 space-y-3 bg-blue-50/30">
          {followUps.map((fq) => (
            <QuestionCard
              key={fq.id}
              q={fq}
              row={answersByQid.get(Number(fq.id)) || null}
              locked={locked}
              inspectionId={inspectionId}
              token={token}
              user={user}
              queueSave={queueSave}
              isFollowUp={true}
              depth={(depth || 0) + 1}
              allQuestions={allQuestions}
              answersByQid={answersByQid}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

export default function QuestionsPanel() {
  const {
    inspectionId, inspection, token, user,
    template, answersByQid, queueSave, flushSave,
    setInspection,
  } = useOutletContext();

  const [submitting, setSubmitting]     = useState(false);
  const [reopening, setReopening]       = useState(false);
  const [submitError, setSubmitError]   = useState(null);
  const [unanswered, setUnanswered]     = useState(null); // list from backend

  const status  = inspection?.status;
  const locked  = status === "questions_submitted" || status === "completed";
  const questionsLocked = inspection?.questions_locked === true;

  // Flat list of all questions (for follow-up lookup)
  const allQuestions = useMemo(() => {
    if (!template?.sections) return [];
    return template.sections.flatMap((sec) => sec.questions || []);
  }, [template]);

  // Only top-level questions (no parent) per section
  const topLevelBySection = useMemo(() => {
    const map = new Map();
    if (!template?.sections) return map;
    for (const sec of template.sections) {
      map.set(sec.id, (sec.questions || []).filter((q) => !q.parent_id));
    }
    return map;
  }, [template]);

  // Progress: required top-level questions answered
  const { answeredRequired, totalRequired } = useMemo(() => {
    let answered = 0, total = 0;
    for (const q of allQuestions) {
      if (!q.required || q.parent_id) continue;
      total++;
      const row = answersByQid.get(Number(q.id));
      if (hasAnswer((q.type || "").toLowerCase(), row)) answered++;
    }
    return { answeredRequired: answered, totalRequired: total };
  }, [allQuestions, answersByQid]);

  const allRequiredAnswered = totalRequired > 0 && answeredRequired === totalRequired;

  async function handleSubmit() {
    setSubmitError(null);
    setUnanswered(null);
    // Flush any pending autosaves first
    await flushSave();
    setSubmitting(true);
    try {
      const result = await api(
        `/india/inspections/${inspectionId}/submit-questions`,
        { method: "POST", token }
      );
      if (result.submitted === false) {
        setUnanswered(result.unanswered || []);
        return;
      }
      // Update inspection status in parent
      setInspection((prev) => ({ ...prev, status: result.status }));
    } catch (e) {
      setSubmitError(e?.message || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleReopen() {
    setSubmitError(null);
    setReopening(true);
    try {
      const result = await api(
        `/india/inspections/${inspectionId}/reopen-questions`,
        { method: "POST", token }
      );
      setInspection((prev) => ({ ...prev, status: result.status }));
      setUnanswered(null);
    } catch (e) {
      setSubmitError(e?.message || "Reopen failed");
    } finally {
      setReopening(false);
    }
  }

  if (!template?.sections?.length) {
    return <div className="text-sm text-gray-500 italic">No template loaded.</div>;
  }

  return (
    <div className="space-y-6">

      {/* Status bar */}
      <div className={[
        "rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3",
        locked
          ? "bg-green-50 border border-green-200"
          : "bg-white border border-gray-200",
      ].join(" ")}>
        <div className="flex flex-wrap items-center gap-3">
          {locked ? (
            <>
              <span className="text-green-700 text-sm font-medium">✓ Questions submitted</span>
              <span className="text-xs text-green-600">
                Inspection Report can now be generated.
              </span>
            </>
          ) : (
            <>
              <div className="text-sm text-gray-700 font-medium">
                {answeredRequired} / {totalRequired} required questions answered
              </div>
              {/* Progress bar */}
              <div className="w-32 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gray-800 rounded-full transition-all"
                  style={{ width: totalRequired ? `${(answeredRequired / totalRequired) * 100}%` : "0%" }}
                />
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          {locked ? (
            questionsLocked ? (
              <span className="text-xs text-gray-400 italic px-3 py-1.5">
                Questions locked after Inspection Report is generated
              </span>
            ) : (
            <button
              type="button"
              onClick={handleReopen}
              disabled={reopening || status === "completed"}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-white bg-green-50 disabled:opacity-40 transition"
              title={status === "completed" ? "Inspection is completed — cannot reopen" : "Reopen for editing"}
            >
              {reopening ? "Reopening…" : "Re-open questions"}
            </button>
            )
          ) : (
            <div className="flex flex-col items-end gap-1">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !allRequiredAnswered}
                className={[
                  "px-4 py-1.5 text-sm rounded-lg font-medium transition",
                  allRequiredAnswered
                    ? "bg-gray-900 text-white hover:bg-gray-700"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed",
                ].join(" ")}
                title={!allRequiredAnswered ? "Answer all required questions first" : "Submit answers — you can re-open questions until you create the Inspection Report"}
              >
                {submitting ? "Submitting…" : "Submit answers"}
              </button>
              <span className="text-xs text-gray-400 italic">
                You can re-open questions until you create the Inspection Report.
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Error / unanswered warnings */}
      {submitError && (
        <div className="text-sm text-red-700 border border-red-200 bg-red-50 rounded-lg px-4 py-3">
          {submitError}
        </div>
      )}

      {unanswered && unanswered.length > 0 && (
        <div className="text-sm border border-amber-200 bg-amber-50 rounded-lg px-4 py-3">
          <div className="font-medium text-amber-800 mb-1">
            {unanswered.length} required question{unanswered.length > 1 ? "s" : ""} still unanswered:
          </div>
          <ul className="text-amber-700 space-y-0.5">
            {unanswered.map((q) => (
              <li key={q.id} className="flex gap-1.5 items-baseline">
                {q.code && <span className="font-mono text-xs shrink-0">{q.code}</span>}
                <span>{q.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Questions by section */}
      {template.sections.map((sec) => {
        const sectionQs = topLevelBySection.get(sec.id) || [];
        return (
          <div key={sec.id} className="space-y-3">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              {sec.title || sec.name}
              <span className="text-xs font-normal text-gray-400">
                ({sectionQs.length} question{sectionQs.length !== 1 ? "s" : ""})
              </span>
            </h3>

            {sectionQs.map((q) => (
              <QuestionCard
                key={q.id}
                q={q}
                row={answersByQid.get(Number(q.id)) || null}
                locked={locked}
                inspectionId={inspectionId}
                token={token}
                user={user}
                queueSave={queueSave}
                isFollowUp={false}
                depth={0}
                allQuestions={allQuestions}
                answersByQid={answersByQid}
              />
            ))}
          </div>
        );
      })}

      {/* Bottom action bar — mirrors the top status bar */}
      <div className={[
        "rounded-xl px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3",
        locked
          ? "bg-green-50 border border-green-200"
          : "bg-white border border-gray-200",
      ].join(" ")}>
        <div className="flex items-center gap-3">
          {locked ? (
            <span className="text-green-700 text-sm font-medium">✓ All answers submitted</span>
          ) : (
            <div className="text-sm text-gray-700 font-medium">
              {answeredRequired} / {totalRequired} required questions answered
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {locked ? (
            questionsLocked ? (
              <span className="text-xs text-gray-400 italic px-3 py-1.5">
                Questions locked after Inspection Report is generated
              </span>
            ) : (
              <button
                type="button"
                onClick={handleReopen}
                disabled={reopening || status === "completed"}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-white bg-green-50 disabled:opacity-40 transition"
                title={status === "completed" ? "Inspection is completed — cannot reopen" : "Reopen for editing"}
              >
                {reopening ? "Reopening…" : "Re-open questions"}
              </button>
            )
          ) : (
            <div className="flex flex-col items-end gap-1">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !allRequiredAnswered}
                className={[
                  "px-4 py-1.5 text-sm rounded-lg font-medium transition",
                  allRequiredAnswered
                    ? "bg-gray-900 text-white hover:bg-gray-700"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed",
                ].join(" ")}
                title={!allRequiredAnswered ? "Answer all required questions first" : "Submit answers — you can re-open questions until you create the Inspection Report"}
              >
                {submitting ? "Submitting…" : "Submit answers"}
              </button>
              <span className="text-xs text-gray-400 italic">
                You can re-open questions until you create the Inspection Report.
              </span>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
